/**
 * `useSubscriptions` - headless state for the "Subscriptions" screen: stats, the
 * filtered site list, the Stripe gateway strip and the manual refund flow.
 *
 * No JSX and no component imports: web_admin and web_crm each render their own chrome
 * on top of this hook, so the two apps can look completely different while sharing one
 * copy of the fetching, filtering and error handling.
 *
 * Backend: `GET /api/admin/subscriptions`, `/stats`, `GET /api/admin/billing/status`,
 * `POST /api/admin/billing/sync-plans`, `POST /api/admin/billing/refund`.
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { makeT } from "../i18n";
import type {
  ApiClient,
  BillingProviderStatus,
  BillingSyncSummary,
  Locale,
  SubscriptionRow,
  SubscriptionStats,
  SubscriptionsListResponse,
} from "../types";

/** Stripe Dashboard link to a customer view. */
export function stripeCustomerUrl(customerId: string): string {
  return `https://dashboard.stripe.com/customers/${customerId}`;
}

/** One package chip in the "sites per plan" strip and one row of the plan filter. */
export type SubscriptionPlanOption = SubscriptionStats["byPlan"][number];

/** Inline note after a Stripe plan sync or a manual refund. Text is already translated. */
export interface SubscriptionsSyncMessage {
  ok: boolean;
  text: string;
}

export interface UseSubscriptionsOptions {
  api: ApiClient;
  locale: Locale;
}

export interface UseSubscriptionsResult {
  /** Headline numbers plus the full platform package catalogue. `null` until loaded. */
  stats: SubscriptionStats | null;
  /** Raw list response (carries `total` / `page` / `pageSize`). `null` until loaded. */
  list: SubscriptionsListResponse | null;
  /** `list.rows`, defaulted to an empty array so the chrome does not repeat the guard. */
  rows: SubscriptionRow[];
  /** True while the list request is in flight (starts true, before the first load). */
  loading: boolean;

  /** Free-text search. Applied only when `loadList` is called (the form submits explicitly). */
  q: string;
  setQ: Dispatch<SetStateAction<string>>;
  /** Package code or `"all"`. Changing it reloads the list. */
  planFilter: string;
  setPlanFilter: Dispatch<SetStateAction<string>>;
  /** Subscription status or `"all"`. Changing it reloads the list. */
  statusFilter: string;
  setStatusFilter: Dispatch<SetStateAction<string>>;
  /** `stats.byPlan`, defaulted to an empty array. */
  planOptions: SubscriptionPlanOption[];

  /** Reload the table with the current `q` / `planFilter` / `statusFilter`. */
  loadList: () => Promise<void>;
  /** Alias of `loadList`, for chrome that reads better as `reload()`. */
  reload: () => Promise<void>;
  /** Reload the stat cards and the package chips. Errors are swallowed. */
  loadStats: () => Promise<void>;

  /** Stripe gateway configuration status. `null` until loaded (or when it failed). */
  stripe: BillingProviderStatus | null;
  syncing: boolean;
  /** Result note of the last sync OR the last refund - the two share one slot. */
  syncMsg: SubscriptionsSyncMessage | null;
  /** Push the package catalogue to Stripe, then re-read the gateway status. */
  syncStripe: () => Promise<void>;

  /** Row awaiting refund confirmation, or `null` when the confirm dialog is closed. */
  refunding: SubscriptionRow | null;
  setRefunding: Dispatch<SetStateAction<SubscriptionRow | null>>;
  refundBusy: boolean;
  /** Refund the account behind `refunding`, then reload the list. Clears `refunding`. */
  doRefund: () => Promise<void>;
}

export function useSubscriptions({ api, locale }: UseSubscriptionsOptions): UseSubscriptionsResult {
  const t = makeT(locale);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [list, setList] = useState<SubscriptionsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refunding, setRefunding] = useState<SubscriptionRow | null>(null);
  const [refundBusy, setRefundBusy] = useState(false);
  const [stripe, setStripe] = useState<BillingProviderStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<SubscriptionsSyncMessage | null>(null);

  async function loadStats() {
    try { setStats(await api.get<SubscriptionStats>("/admin/subscriptions/stats")); } catch { /* ignore */ }
  }

  async function loadStripe() {
    try { setStripe(await api.get<BillingProviderStatus>("/admin/billing/status")); } catch { /* ignore */ }
  }

  async function syncStripe() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await api.post<BillingSyncSummary>("/admin/billing/sync-plans");
      setSyncMsg({ ok: true, text: `${t("subs.stripe.syncDone")} (${res.synced})` });
      await loadStripe();
    } catch {
      setSyncMsg({ ok: false, text: t("subs.stripe.syncFailed") });
    } finally {
      setSyncing(false);
    }
  }

  async function doRefund() {
    if (!refunding?.accountId) return;
    setRefundBusy(true);
    try {
      await api.post("/admin/billing/refund", { accountId: refunding.accountId });
      setSyncMsg({ ok: true, text: t("subs.refundDone") });
      await loadList();
    } catch {
      setSyncMsg({ ok: false, text: t("subs.refundFailed") });
    } finally {
      setRefundBusy(false);
      setRefunding(null);
    }
  }

  async function loadList() {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (planFilter !== "all") params.set("planCode", planFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    try {
      setList(await api.get<SubscriptionsListResponse>(`/admin/subscriptions?${params.toString()}`));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadStats(); void loadStripe(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { void loadList(); /* eslint-disable-next-line */ }, [planFilter, statusFilter]);

  const planOptions = stats?.byPlan ?? [];
  const rows = list?.rows ?? [];

  return {
    stats, list, rows, loading,
    q, setQ, planFilter, setPlanFilter, statusFilter, setStatusFilter, planOptions,
    loadList, reload: loadList, loadStats,
    stripe, syncing, syncMsg, syncStripe,
    refunding, setRefunding, refundBusy, doRefund,
  };
}
